import { Dialog, DialogContent } from "@/components/ui/dialog";
import { usePopupStore } from "@/stores/popup.store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Confetti from "react-confetti";
import { useEffect, useState } from "react";
import { useSeenWelcomePopup, useDismissTour } from "@/hooks/use-organisation";
import { Activity, Inbox, Mail } from "lucide-react";
import { Lightning } from "@phosphor-icons/react";
import { Card } from "../ui/card";

export function WelcomePopup({
  isOpen,
  closePopup,
}: {
  isOpen: boolean;
  closePopup: () => void;
}) {
  const [showConfetti, setShowConfetti] = useState(false);
  const { mutate: seenWelcomePopup } = useSeenWelcomePopup();
  const { mutate: dismissTour } = useDismissTour();
  const { setIsTourSkipped } = usePopupStore();

  // Only show confetti when popup opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Stop confetti after 3 seconds
      const timer = setTimeout(() => setShowConfetti(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Get window size for confetti
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    function handleResize() {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleOpenChange = (open: boolean) => {
    if (!open) return; // Prevent closing
  };

  return (
    <>
      {showConfetti && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-[60]">
          <Confetti
            width={dimensions.width}
            height={dimensions.height}
            numberOfPieces={1000}
            recycle={false}
          />
        </div>
      )}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="max-w-2xl bg-background [data-dialog-close]:hidden max-h-[85vh] overflow-y-auto"
        >
          <div className="flex flex-col items-center">
            {/* Header */}
            <div className="w-full text-center mb-6 rounded-lg p-4">
              <h2 className="text-3xl font-bold mb-2">
                Welcome to InboxDoctor!
              </h2>
              <p className="text-muted-foreground text-base">
                Human-Powered Email Warmups
                <br />
                Your 7 days free trial starts from today!
              </p>
            </div>

            {/* Cards */}
            <div className="space-y-4 w-full">
              {/* Mailboxes */}
              <Card className="rounded-2xl p-4 flex flex-col gap-2 bg-orange-50 dark:bg-orange-950 border-orange-100 dark:border-orange-900">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-orange-900">
                    <Mail className="size-5" />
                    <span className="font-semibold">
                     20 Mailboxes - B2B
                    </span>
                  </div>
                  <Badge className="ml-2 text-xs bg-orange-100 dark:bg-orange-950 text-orange-500 hover:!bg-orange-100 shadow-none border-none">
                    Free Gift
                  </Badge>
                </div>
                <p className="text-sm text-orange-800">
                  Connect up to 20 B2B mailboxes and send 50 warmup emails per day for 7 days to warm up your email infrastructure.
                </p>
              </Card>
              {/* Placement Tests */}
              <Card className="rounded-2xl p-4 flex flex-col gap-2 bg-blue-50 dark:bg-blue-950 border-blue-100 dark:border-blue-900">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-blue-900">
                    <Inbox className="size-5" />

                    <span className="font-medium">
                      1 Free Inbox Placement Tests
                    </span>
                  </div>
                  <Badge className="ml-2 text-xs bg-blue-50 dark:bg-blue-950 text-blue-500 shadow-none border-none hover:!bg-blue-50">
                    Free Gift
                  </Badge>
                </div>
                <p className="text-sm text-blue-800">
                  Ensure your emails land in the inbox, not spam, across email
                  providers.
                </p>
              </Card>

              {/* Health Tests */}
              <Card className="rounded-2xl p-4 flex flex-col gap-2 bg-green-50 dark:bg-green-950 border-green-100 dark:border-green-900">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-900">
                    <Activity className="size-5" />
                    <span className="font-semibold">
                      2 Free Email Health Tests
                    </span>
                  </div>
                  <Badge className="ml-2 text-xs bg-green-100 dark:bg-green-950 text-green-500 hover:!bg-green-100 shadow-none border-none">
                    Free Gift
                  </Badge>
                </div>
                <p className="text-sm text-green-800">
                  Email Health Tests is useful to check and optimize your sender
                  reputation.
                </p>
              </Card>

              {/* Product Tour */}
              <div className="bg-purple-50 dark:bg-purple-950 border border-purple-100 dark:border-purple-900 rounded-xl p-4 flex flex-col gap-2 ">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-purple-900">
                    <Lightning className="size-5" />
                    <span className="font-semibold">
                      Discover InboxDoctor Magic
                    </span>
                  </div>
                  <Badge className="ml-2 text-xs bg-purple-200 text-purple-600 shadow-none border-none hover:!bg-purple-50">
                    Tour
                  </Badge>
                </div>
                <p className="text-sm text-purple-800">
                  Take a quick tour to unlock all the powerful features waiting
                  for you.
                </p>
                {/* <span className="text-sm text-purple-600  underline cursor-pointer">
                  Start now
                </span> */}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 mt-8 w-full">
              <Button
                variant="outline"
                onClick={() => {
                  seenWelcomePopup();
                  dismissTour();
                  setIsTourSkipped(true);
                  closePopup();
                }}
              >
                Skip Tour
              </Button>
              <Button
                onClick={() => {
                  seenWelcomePopup();
                  closePopup();
                }}
              >
                Start Product Tour
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
